import { zodResolver } from "@hookform/resolvers/zod"
import clsx from "clsx"
import { useEffect, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { useAssociateToCompanyMutation, useDissociateEmployeeMutation } from "../../../../../features/peopleApi"
import { addBannerWithTimeout } from "../../../../lib/centralizedBanner/slice"
import { useAppDispatch } from "../../../../lib/redux/hooks"
import { colors } from "../../../../ui/colors"
import { PlusIcon } from "../../../../ui/icons/plus"
import { XCircleIcon } from "../../../../ui/icons/xCircle"
import Button from "../../../../ui/molecules/button"
import Input from "../../../../ui/molecules/input/Input"
import { Spinner } from "../../../../ui/molecules/spinner"
import Toggle from "../../../../ui/molecules/toggle"
import { SelectCompany } from "../../select/companySelect"
import { PersonDTO } from "../dto"
import { AssociateToCompanyFormValues, createAssociateToCompanySchema } from "./validation"

interface Props {
    person?: PersonDTO;
    operation?: 'associate' | 'dissociate';
    visible: boolean;
    onClose: () => void;
}

export function AssociateCompanyModal(props: Props) {
    const { person, operation, visible, onClose } = props

    const [associate, { isLoading: isAssociating }] = useAssociateToCompanyMutation()
    const [dissociate, { isLoading: isDissociating }] = useDissociateEmployeeMutation()

    const [notFound, setNotFound] = useState<boolean>(false)

    const form = useForm<AssociateToCompanyFormValues>({
        resolver: zodResolver(createAssociateToCompanySchema(notFound)),
        defaultValues: {
            companyId: undefined,
            companyName: '',
            companyVat: '',
            companyPhone: []
        },
    });


    const phones = form.watch("companyPhone") || ['']
    const errors = form.formState.errors.companyPhone

    const updatePhone = (index: number, value: string) => {
        const updatedPhones = [...phones]
        updatedPhones[index] = value
        form.setValue("companyPhone", updatedPhones, { shouldValidate: true })
    }

    const removePhone = (index: number) => {
        const updatedPhones = phones.filter((_, i) => i !== index)
        if (updatedPhones.length === 0) {
            form.setValue("companyPhone", [''], { shouldValidate: true })
        } else {
            form.setValue("companyPhone", updatedPhones, { shouldValidate: true })
        }
    }

    const addPhone = () => {
        form.setValue("companyPhone", [...phones, ''], { shouldValidate: true })
    }

    const dispatch = useAppDispatch()

    const handleSubmit = async () => {
        if (operation === 'associate') {
            try {
                await associate({
                    data: { ...form.getValues(), companyPhone: form.getValues().companyPhone ?? [], companyName: form.getValues().companyName ?? undefined },
                    personId: person?.id || ''
                }).unwrap();
                dispatch(addBannerWithTimeout({
                    message: "Contatto associato con successo",
                    variant: "success"
                }));
            } catch (error) {
                dispatch(addBannerWithTimeout({
                    message: "Errore durante l'associazione del contatto",
                    variant: "error"
                }));
            }
        } else {
            try {
                await dissociate({
                    data: { ...form.getValues(), companyPhone: form.getValues().companyPhone ?? [], companyName: form.getValues().companyName ?? undefined },
                    personId: person?.id || ''
                }).unwrap();
                onClose()
                dispatch(addBannerWithTimeout({
                    message: "Contatto dissociato con successo",
                    variant: "success"
                }));
            } catch (error) {
                dispatch(addBannerWithTimeout({
                    message: "Errore durante la dissociazione del contatto",
                    variant: "error"
                }));
            }
        }
    };

    useEffect(() => {
        if (notFound) {
            form.setValue("companyId", undefined, { shouldValidate: true })
        }
    }, [notFound, form])

    return (
        <div
            id="associate-company-modal"
            key={"associate-company-modal"}
            onClick={() => onClose()}
            className={clsx("transition-all fixed bg-opacity-30 p-5 bg-neutral-950 backdrop-blur-sm top-0 left-0 h-full w-full flex items-center justify-center z-[100]", {
                "block": visible,
                "hidden": !visible
            })} >
            <div
                onClick={e => {
                    e.stopPropagation()
                }}
                className="transition-all max-h-full flex flex-col w-[460px] relative items-center bg-white rounded-[12px] opacity-100 shadow-sm min-w-[400px]">
                <div
                    onClick={() => onClose()}
                    className="absolute cursor-pointer rotate-45 top-[-16px] right-[-16px] border border-brandPrimary-600 h-8 w-8 rounded-full flex items-center justify-center bg-brandPrimary-50"
                >
                    <PlusIcon color={colors.brandPrimary[600]} size={16} />
                </div>
                <div className="p-6 text-heading-xs font-semibold text-center">
                    Associa un'azienda a {person?.name + ' ' + person?.surname}
                </div>
                <div className="px-6 h-full overflow-auto py-8 flex flex-col justify-center items-center gap-6 border-t-neutral-200 border-t">
                    <span className="text-text-md font-medium text-neutral-700 text-start justify-start items-start w-full mt-3">
                        Seleziona un'azienda esistente o crea una nuova
                    </span>
                    <Controller
                        name="companyId"
                        control={form.control}
                        render={({ field }) => (
                            <SelectCompany
                                isMulti={false}
                                error={!!form.formState.errors.companyId}
                                errorLabel={form.formState.errors.companyId ? form.formState.errors.companyId.message : ""}
                                id="associate-company-select"
                                disabled={notFound}
                                placeholder={"Cerca per ragione sociale"}
                                value={field.value}
                                onChange={e => field.onChange(e)}
                            />
                        )}
                    />
                    <Toggle
                        disabled={false}
                        placeholder="L'azienda che cerco non è presente in elenco"
                        checked={notFound}
                        onChange={function (value: boolean): void {
                            setNotFound(value)
                        }}
                    />
                    {
                        notFound &&
                        <>
                            <span className="text-text-md font-medium text-neutral-700 text-start justify-start items-start w-full mt-6">
                                Inserisci i dettagli della nuova azienda
                            </span>
                            <Controller
                                name="companyName"
                                control={form.control}
                                render={({ field }) => (
                                    <Input
                                        onChange={(e) => field.onChange(e)}
                                        label="Nome azienda *"
                                        placeholder="Inserisci il nome dell'azienda"
                                        defaultValue={field.value ?? undefined}
                                        error={!!form.formState.errors.companyName}
                                        supportingText={form.formState.errors.companyName ? "Inserire il nome dell'azienda" : ""}
                                    />
                                )}
                            />
                            <div className="w-full flex flex-col gap-2">
                                <span className="text-left w-full flex justify-between items-center input-label">
                                    Numero di telefono dell'azienda *
                                </span>
                                <div className="flex flex-col gap-3">
                                    {
                                        phones.map((phone, index) => {
                                            const fieldError = Array.isArray(errors) ? errors[index] : undefined
                                            return (
                                                <div key={index + '-div'} className="flex w-full gap-3 items-start">
                                                    <Input
                                                        key={index + '-input'}
                                                        placeholder="Inserisci il numero di telefono dell'azienda"
                                                        value={phone}
                                                        error={!!fieldError || phones.length === 0}
                                                        supportingText={fieldError?.message || ''}
                                                        onChangeText={(text) => {
                                                            updatePhone(index, text)
                                                        }}
                                                    />
                                                    <div className="h-10 flex items-center justify-center">
                                                        <Button
                                                            size={"sm"}
                                                            iconPosition={"only"}
                                                            icon={<XCircleIcon color={""} size={0} />}
                                                            variant={"ghost"}
                                                            color={"blue"}
                                                            onClick={() => {
                                                                removePhone(index)
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            )
                                        })
                                    }
                                </div>
                                <div className="flex items-end justify-end">
                                    <Button
                                        size={"sm"}
                                        iconPosition={"left"}
                                        label="Aggiungi telefono"
                                        variant={"outline"}
                                        color={"blue"}
                                        onClick={() => addPhone()}
                                        icon={<PlusIcon color={colors.brandPrimary[600]} size={0} />}
                                    />
                                </div>
                            </div>
                            <Controller
                                name="companyVat"
                                control={form.control}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        label="P. IVA"
                                        placeholder="Inserisci la P. IVA dell'azienda"
                                        defaultValue={field.value}
                                        error={!!form.formState.errors.companyVat}
                                        supportingText={form.formState.errors.companyVat ? "Inserire una P. IVA valida" : ""}
                                    />
                                )}
                            />
                        </>
                    }
                    {
                        isAssociating || isDissociating ?
                            <Spinner size={32} /> :
                            <div className="flex gap-4 w-full mt-6">
                                <Button size={"sm"} iconPosition={"off"} variant={"outline"} color={"gray"} label="Annulla" fullWidth onClick={() => {
                                    onClose()
                                }} />
                                <Button size={"sm"} iconPosition={"off"} variant={"solid"} color={"blue"} label="Associa azienda" fullWidth
                                    onClick={() => form.handleSubmit(handleSubmit)()}
                                />
                            </div>
                    }
                </div>
            </div>
        </div>
    )
}